import React, { useCallback } from 'react';
import * as Yup from 'yup';
import { Modal, ModalHeader, ModalBody, Col, Row } from 'reactstrap';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import FormInputField from '../../common/components/formFields/FormInputField';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import { FormSelectField } from '../../common/components/formFields';

const validationSchema = Yup.object().shape({
  currentTitle: Yup.mixed().required('Field is required'),
  currentFirstName: Yup.string().required('Field is required'),
  currentLastName: Yup.string().required('Field is required'),
  currentEmail: Yup.string().required('Field is required').email('Email has the wrong format'),
  currentContactNumber: Yup.string().required('Field is required'),
  currentAddress1: Yup.string().required('Field is required'),
  currentPostCode: Yup.string().required('Field is required'),
  phoneMarketingAllowed: Yup.mixed().required('Field is required'),
  emailMarketingAllowed: Yup.mixed().required('Field is required'),
  smsMarketingAllowed: Yup.mixed().required('Field is required'),
  postMarketingAllowed: Yup.mixed().required('Field is required'),
});

const initialValues = {
  currentTitle: null,
  currentFirstName: null,
  currentLastName: null,
  currentEmail: null,
  currentContactNumber: null,
  currentAddress1: null,
  currentAddress2: null,
  currentAddress3: null,
  currentPostCode: null,
  phoneMarketingAllowed: null,
  emailMarketingAllowed: null,
  smsMarketingAllowed: null,
  postMarketingAllowed: null,
};

function RemoveMyDataModal({ isOpen, toggle, onSave }) {
  const handleSubmit = useCallback(
    async (values, { resetForm }) => {
      await onSave(values);
      resetForm();
      toggle();
    },
    [onSave, toggle],
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="lg"
      backdropClassName="add-address-modal-backdrop"
      className="add-address-modal"
    >
      <ModalHeader toggle={toggle} tag="div">
        <SettingsSectionHeader
          header="Remove My Data"
          variant="noBorder"
          headerClassName="add-address-modal-header"
          description="This will stop all sales and service marketing communications, including Service and MOT reminders."
        />
      </ModalHeader>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <ModalBody>
              <p className="modal-section-title">Section 1 - Your current details</p>

              <Row className="">
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentTitle"
                    label="Current Title"
                    labelVariant="bordered"
                    required
                  />
                </Col>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentFirstName"
                    label="Current First Name"
                    labelVariant="bordered"
                    required
                  />
                </Col>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentLastName"
                    label="Current Last Name"
                    labelVariant="bordered"
                    required
                  />
                </Col>
              </Row>
              <Row>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentEmail"
                    label="Current Email"
                    labelVariant="bordered"
                    required
                  />
                </Col>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentContactNumber"
                    label="Current Contact Number"
                    labelVariant="bordered"
                    required
                  />
                </Col>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentAddress1"
                    label="Current Address 1"
                    labelVariant="bordered"
                    required
                  />
                </Col>
              </Row>
              <Row>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentAddress2"
                    label="Current Address 2"
                    labelVariant="bordered"
                  />
                </Col>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentAddress3"
                    label="Current Address 3"
                    labelVariant="bordered"
                  />
                </Col>
                <Col md={4} className="mt-1">
                  <FormInputField
                    name="currentPostCode"
                    label="Current Post Code"
                    labelVariant="bordered"
                    required
                  />
                </Col>
              </Row>
              <p className="modal-section-title">Your marketing choices</p>
              <Row>
                <Col md={3} className="mt-1">
                  <FormSelectField
                    name="postMarketingAllowed"
                    label="Post"
                    labelVariant="bordered"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    required
                  />
                </Col>
                <Col md={3} className="mt-1">
                  <FormSelectField
                    name="emailMarketingAllowed"
                    label="Email"
                    labelVariant="bordered"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    required
                  />
                </Col>
                <Col md={3} className="mt-1">
                  <FormSelectField
                    name="smsMarketingAllowed"
                    label="SMS"
                    labelVariant="bordered"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    required
                  />
                </Col>
                <Col md={3} className="mt-1">
                  <FormSelectField
                    name="phoneMarketingAllowed"
                    label="Phone"
                    labelVariant="bordered"
                    options={[
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' },
                    ]}
                    required
                  />
                </Col>
              </Row>
            </ModalBody>
            <Row className="mb-3 add-address-modal-buttons submit-buttons">
              <Col xs={6}>
                <CommonButton
                  label="SUBMIT"
                  variant={CommonButtonVariants.PRIMARY_RED}
                  type="submit"
                  disabled={isSubmitting || !isValid || !dirty}
                  className="w-100"
                />
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

RemoveMyDataModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default RemoveMyDataModal;
